// Evidence levels A–D for long-form reports: the four-level scale analysts publish by ("A: publishable as is",
// "B: publishable with attribution", "C: to corroborate", "D: internal only"), derived from what Hoopoe already
// records about a claim: review status, kind (fact, allegation, assessment), attribution, the A–F × 1–6 reliability
// grade, how many independent sources back it and its reliability status (`reliability.ts`). A mapping, not a new
// field: it changes as the claim is reviewed and as its sources are registered.

import { type AttributionLevel, type ClaimKind, parseReliability, type ReviewStatus, type SourceReliability } from './claims';
import { DOSSIER_LABELS } from './dossier-labels';
import type { ReliabilityStatus } from './reliability';
import type { ReportLanguage } from './report-language';

export const EVIDENCE_LEVELS = ['A', 'B', 'C', 'D'] as const;
export type EvidenceLevel = (typeof EVIDENCE_LEVELS)[number];

/** What each level means for publication (the legend of a full report; French in dossier-labels.ts). */
export const EVIDENCE_LEVEL_MEANINGS: Record<EvidenceLevel, string> = DOSSIER_LABELS.en.evidenceMeanings;

export interface EvidenceFacts {
  status: ReviewStatus;
  claimKind: ClaimKind;
  attributionLevel: AttributionLevel;
  /** Reliability grade such as "B2", or null. */
  grade?: string | null;
  /**
   * Independent sources backing the claim (source registry groups: copies of one story, outlets of one owner and
   * the actor's own sources don't add up), not evidence rows.
   */
  evidenceCount?: number;
  /** Reliability status: an official document behind it counts like two independent sources. */
  reliability?: ReliabilityStatus | null;
}

/**
 * The letter and number of a reliability grade as stored or written: "B2", "b2", "B2 — Moderate", "B" alone, "2"
 * alone. Missing or unreadable → both null.
 */
export function gradeParts(grade: string | null | undefined): { source: SourceReliability | null; credibility: number | null } {
  const raw = grade?.trim() ?? '';
  if (!raw) return { source: null, credibility: null };
  const only = raw.match(/^([1-6])(?![\p{L}\p{N}])/u);
  if (only) return { source: null, credibility: Number(only[1]) };
  const g = parseReliability(raw);
  return { source: g?.source ?? null, credibility: g?.credibility ?? null };
}

/**
 * The evidence level of a claim (see EVIDENCE_LEVEL_MEANINGS). D wins over everything: a grade of E/F or 5/6, an
 * unreviewed allegation, an anonymous attribution nobody verified. Then A: a verified fact that is corroborated (two
 * independent sources), backed by an official document, or graded "confirmed" (credibility 1). Then B: any other
 * verified claim, or an unreviewed fact that is corroborated or official with a good source letter (A or B).
 * Everything else is C.
 */
export function evidenceLevel(c: EvidenceFacts): EvidenceLevel {
  const { source, credibility } = gradeParts(c.grade);
  const count = c.evidenceCount ?? 0;
  const verified = c.status === 'verified';
  const backed = count >= 2 || c.reliability === 'corroborated' || c.reliability === 'official_document';
  if (source === 'E' || source === 'F' || credibility === 5 || credibility === 6) return 'D';
  if (c.status === 'unreviewed' && c.claimKind === 'allegation') return 'D';
  if (c.attributionLevel === 'anonymous' && !verified) return 'D';
  if (verified && c.claimKind === 'fact' && (backed || credibility === 1)) return 'A';
  if (verified) return 'B';
  if (c.status === 'unreviewed' && c.claimKind === 'fact' && backed && (source === 'A' || source === 'B')) return 'B';
  return 'C';
}

/** The legend of a full report, in its language (English by default). */
export const evidenceLegend = (lang: ReportLanguage = 'en'): { level: EvidenceLevel; meaning: string }[] =>
  EVIDENCE_LEVELS.map(level => ({ level, meaning: DOSSIER_LABELS[lang].evidenceMeanings[level] }));
